/**
 * Cart page promo code element.
 * - Applies/removes discount codes via the Cart AJAX API (/cart/update.js "discount" property).
 * - Fires a red, white & blue fireworks + confetti celebration when a code listed in
 *   data-celebrate-codes (e.g. "USA250") is applied — whether typed into the promo box
 *   or arriving via a /discount/USA250 link (once per session).
 */

/* ------------------------------------------------------------------ */
/* Celebration: fireworks + confetti (USA 250th — red, white & blue)  */
/* ------------------------------------------------------------------ */
window.wwCartCelebration = window.wwCartCelebration || (function celebrationFactory() {
  const COLORS = ['#e0162b', '#ffffff', '#0a3161', '#b22234', '#3c6cc9'];
  let canvas;
  let ctx;
  let parts = [];
  let rockets = [];
  let running = false;

  function ensureCanvas() {
    if (canvas) return;
    canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:9999;display:none;';
    document.body.appendChild(canvas);
    ctx = canvas.getContext('2d');
  }

  function burst(x, y, count, power) {
    for (let i = 0; i < count; i += 1) {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * power + 2;
      parts.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        gravity: 0.16,
        drag: 0.985,
        r: Math.random() * 3 + 1.5,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        alpha: 1,
        fade: 0.012 + Math.random() * 0.012,
        shape: 'spark'
      });
    }
  }

  function confettiRain(count) {
    for (let i = 0; i < count; i += 1) {
      parts.push({
        x: Math.random() * window.innerWidth,
        y: -20 - Math.random() * window.innerHeight * 0.4,
        vx: (Math.random() - 0.5) * 2.5,
        vy: Math.random() * 2.5 + 2,
        gravity: 0.05,
        drag: 1,
        r: Math.random() * 6 + 3,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        rot: Math.random() * Math.PI * 2,
        rv: (Math.random() - 0.5) * 0.3,
        alpha: 1,
        fade: 0.005 + Math.random() * 0.005,
        shape: Math.random() > 0.5 ? 'rect' : 'circle'
      });
    }
  }

  function launchRockets(count) {
    for (let i = 0; i < count; i += 1) {
      rockets.push({
        x: window.innerWidth * (0.15 + Math.random() * 0.7),
        y: window.innerHeight + 10,
        vy: -(Math.random() * 5 + 13),
        targetY: window.innerHeight * (0.15 + Math.random() * 0.3),
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        delay: i * 18 + Math.floor(Math.random() * 12)
      });
    }
  }

  function frame() {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    rockets = rockets.filter((rk) => {
      if (rk.delay > 0) {
        rk.delay -= 1;
        return true;
      }
      rk.y += rk.vy;
      rk.vy += 0.12;
      ctx.save();
      ctx.globalAlpha = 0.9;
      ctx.fillStyle = rk.color;
      ctx.beginPath();
      ctx.arc(rk.x, rk.y, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 0.35;
      ctx.fillRect(rk.x - 1, rk.y, 2, 16);
      ctx.restore();
      if (rk.y <= rk.targetY || rk.vy >= -2) {
        burst(rk.x, rk.y, 70, 7);
        return false;
      }
      return true;
    });

    parts = parts.filter((p) => {
      p.vx *= p.drag;
      p.vy = p.vy * p.drag + p.gravity;
      p.x += p.vx;
      p.y += p.vy;
      if (p.rot !== undefined) p.rot += p.rv;
      p.alpha -= p.fade;
      if (p.alpha <= 0 || p.y > canvas.height + 30) return false;

      ctx.save();
      ctx.globalAlpha = Math.max(p.alpha, 0);
      ctx.fillStyle = p.color;
      if (p.shape === 'rect') {
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillRect(-p.r, -p.r / 2, p.r * 2, p.r);
      } else if (p.shape === 'circle') {
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.beginPath();
        ctx.arc(0, 0, p.r / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
      return true;
    });

    if (parts.length > 0 || rockets.length > 0) {
      requestAnimationFrame(frame);
    } else {
      canvas.style.display = 'none';
      running = false;
    }
  }

  return function celebrate() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    ensureCanvas();
    canvas.style.display = 'block';
    launchRockets(6);
    confettiRain(140);
    // A couple of instant bursts so it pops immediately
    burst(window.innerWidth * 0.3, window.innerHeight * 0.3, 60, 6);
    burst(window.innerWidth * 0.7, window.innerHeight * 0.25, 60, 6);
    if (!running) {
      running = true;
      requestAnimationFrame(frame);
    }
  };
}());

/* ------------------------------------------------------------------ */
/* Promo code custom element                                           */
/* ------------------------------------------------------------------ */
if (!customElements.get('cart-promo-code')) {
  class CartPromoCode extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.js-promo-input');
      this.applyBtn = this.querySelector('.js-promo-apply');
      this.errorEl = this.querySelector('.js-promo-error');

      if (this.applyBtn) {
        this.applyBtn.addEventListener('click', this.apply.bind(this));
      }

      if (this.input) {
        this.input.addEventListener('keydown', (evt) => {
          if (evt.key === 'Enter') {
            evt.preventDefault();
            this.apply();
          }
        });
      }

      // Remove (×) buttons on the discount rows at the top of the Order
      // Summary live outside this element's subtree, so bind them here too.
      document.querySelectorAll('.js-promo-remove-top').forEach((btn) => {
        btn.addEventListener('click', () => this.remove(btn.dataset.code));
      });

      this.checkCelebration();
    }

    /**
     * Currently applied discount codes (rendered by Liquid into data-codes).
     * @returns {string[]}
     */
    get appliedCodes() {
      return (this.dataset.codes || '').split(',').map((c) => c.trim()).filter(Boolean);
    }

    /**
     * Codes that should trigger the fireworks celebration (data-celebrate-codes).
     * @returns {string[]}
     */
    get celebrateCodes() {
      return (this.dataset.celebrateCodes || '').split(',').map((c) => c.trim().toLowerCase()).filter(Boolean);
    }

    /**
     * Returns the matching celebration code if `code` is one, otherwise null.
     * @param {string} code - Discount code to test.
     * @returns {string|null}
     */
    celebrationMatch(code) {
      if (!code) return null;
      return this.celebrateCodes.includes(code.trim().toLowerCase()) ? code.trim().toUpperCase() : null;
    }

    /**
     * Fires the celebration when appropriate:
     * 1. Just applied via the promo box (pre-reload flag in sessionStorage).
     * 2. Arrived with the code already applied (e.g. /discount/USA250 link) — once per session.
     */
    checkCelebration() {
      try {
        const pending = sessionStorage.getItem('ww-celebrate-pending');
        if (pending && this.celebrationMatch(pending)) {
          sessionStorage.removeItem('ww-celebrate-pending');
          sessionStorage.setItem(`ww-celebrated-${pending.toUpperCase()}`, '1');
          setTimeout(window.wwCartCelebration, 350);
          return;
        }

        const applied = this.appliedCodes.find((c) => this.celebrationMatch(c));
        if (applied && !sessionStorage.getItem(`ww-celebrated-${applied.toUpperCase()}`)) {
          sessionStorage.setItem(`ww-celebrated-${applied.toUpperCase()}`, '1');
          setTimeout(window.wwCartCelebration, 350);
        }
      } catch (error) {
        // sessionStorage unavailable (private mode etc.) — fail silently
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }

    /**
     * Sends a discount update to the Cart API.
     * @param {string[]} codes - Full list of codes that should be applied.
     * @returns {Promise<object>} - The updated cart object.
     */
    static async updateDiscounts(codes) {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ discount: codes.join(',') })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Cart update failed');
      return data;
    }

    async apply() {
      const code = this.input ? this.input.value.trim() : '';
      if (!code) return;

      this.hideError();
      this.classList.add('is-loading');

      try {
        const codes = [...this.appliedCodes];
        if (!codes.some((c) => c.toLowerCase() === code.toLowerCase())) {
          codes.push(code);
        }

        const cart = await CartPromoCode.updateDiscounts(codes);
        const applied = (cart.discount_codes || []).find(
          (d) => d.code && d.code.toLowerCase() === code.toLowerCase()
        );

        if (!applied || applied.applicable === false) {
          // Revert to the previously applied codes and show an error.
          await CartPromoCode.updateDiscounts(this.appliedCodes);
          this.classList.remove('is-loading');
          this.showError(`"${code}" isn't a valid code or can't be applied to this cart.`);
          return;
        }

        // Queue the celebration for after the reload if this is a celebration code.
        if (this.celebrationMatch(code)) {
          try {
            sessionStorage.setItem('ww-celebrate-pending', code.toUpperCase());
          } catch (error) { /* ignore */ }
        }

        window.location.reload();
      } catch (error) {
        this.classList.remove('is-loading');
        this.showError('Sorry, something went wrong. Please try again.');
        console.log(error); // eslint-disable-line
      }
    }

    async remove(code) {
      if (!code) return;
      this.hideError();
      this.classList.add('is-loading');

      try {
        const remaining = this.appliedCodes.filter(
          (c) => c.toLowerCase() !== code.toLowerCase()
        );
        await CartPromoCode.updateDiscounts(remaining);

        // Allow the celebration to fire again if the code is re-applied later.
        if (this.celebrationMatch(code)) {
          try {
            sessionStorage.removeItem(`ww-celebrated-${code.toUpperCase()}`);
          } catch (error) { /* ignore */ }
        }

        window.location.reload();
      } catch (error) {
        this.classList.remove('is-loading');
        this.showError('Sorry, something went wrong. Please try again.');
        console.log(error); // eslint-disable-line
      }
    }
  }

  customElements.define('cart-promo-code', CartPromoCode);
}